// src/pages/ChatBot/query-logs/api.ts
//
// Thin client for the query-logs admin API (mounted at /chatbot/query-logs).
// Auth rides on cookies (access_token + id_token), matching the rest of the
// admin console — every call sends credentials: 'include'. Errors surface as
// QueryLogApiError carrying the server's { error } message and HTTP status.

import type {
  CustomAnswerCitation,
  QueryLogDetailResponse,
  QueryLogDoc,
  QueryLogListParams,
  QueryLogListResponse,
} from './types';

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/chatbot/query-logs`;

export class QueryLogApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'QueryLogApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: init?.body
      ? { 'Content-Type': 'application/json', ...init.headers }
      : init?.headers,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') message = body.error;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new QueryLogApiError(message, res.status);
  }
  return res.json() as Promise<T>;
}

export function fetchQueryLogs(params: QueryLogListParams = {}): Promise<QueryLogListResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  // Multiple flags OR together, comma-separated in the single `flag` param.
  if (params.flags && params.flags.length > 0) search.set('flag', params.flags.join(','));
  if (params.evalSet !== undefined) search.set('evalSet', String(params.evalSet));
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.askedBy) search.set('askedBy', params.askedBy);
  // Free-text search: substring on the question / user, ISO-prefix on askedAt.
  if (params.question) search.set('question', params.question);
  if (params.user) search.set('user', params.user);
  if (params.timestamp) search.set('timestamp', params.timestamp);
  if (params.page) search.set('page', String(params.page));
  const qs = search.toString();
  return request<QueryLogListResponse>(qs ? `?${qs}` : '');
}

export function fetchQueryLog(id: string): Promise<QueryLogDetailResponse> {
  return request<QueryLogDetailResponse>(`/${encodeURIComponent(id)}`);
}

// Creates an admin-authored Q&A from scratch (no originating ask). The server
// embeds the question so the saved answer can serve as an override for future
// near-duplicate questions — the created doc carries outcome.served =
// 'admin-created' and derives as Custom. POSTs to the collection root. When
// `evalSet` is true the created Q&A lands directly in the golden set; it is
// omitted from the body otherwise so existing callers keep the prior shape.
export function createCustomQueryLog(
  question: string,
  answer: string,
  citations: CustomAnswerCitation[],
  evalSet?: boolean,
): Promise<{ log: QueryLogDoc }> {
  return request<{ log: QueryLogDoc }>('', {
    method: 'POST',
    body: JSON.stringify({ question, answer, citations, ...(evalSet ? { evalSet: true } : {}) }),
  });
}

export function saveCustomAnswer(
  id: string,
  answer: string,
  citations: CustomAnswerCitation[],
): Promise<{ log: QueryLogDoc }> {
  return request<{ log: QueryLogDoc }>(`/${encodeURIComponent(id)}/answer`, {
    method: 'PUT',
    body: JSON.stringify({ answer, citations }),
  });
}

export function deleteCustomAnswer(id: string): Promise<{ log: QueryLogDoc }> {
  return request<{ log: QueryLogDoc }>(`/${encodeURIComponent(id)}/answer`, {
    method: 'DELETE',
  });
}

// feedback is the numeric thumbs signal: 1 = thumbs up, -1 = thumbs down, 0 = clear the rating.
export function patchQueryLogAudit(
  id: string,
  fields: { evalSet?: boolean; feedback?: 1 | -1 | 0 },
): Promise<{ log: QueryLogDoc }> {
  return request<{ log: QueryLogDoc }>(`/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}

export function deleteQueryLog(id: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
